import {
  QueryKey,
  useQuery as orgUseQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import flattenDeep from "lodash-es/flattenDeep";
import isEmpty from "lodash-es/isEmpty";
import values from "lodash-es/values";
import { TlxHttpResponse } from "@/api";
import getMock from "@/configs/mock";
import { getMockData } from "@/helpers/getMockData";
import { useIsInMockScope } from "@/libs/mockScope";
import { QueryMethod } from "./usePaginateQuery";

export type QueryOptions<Response> = Omit<
  UseQueryOptions<Response, AxiosError<TlxHttpResponse>, Response, QueryKey>,
  "queryKey" | "queryFn"
>;

export interface QueryProps<Args, Response> {
  method: QueryMethod<Args, Response>;
  queryKey?: QueryKey;
  params?: Args;
  options?: QueryOptions<Response>;
}

function useQuery<Args, Response>(args: QueryProps<Args, Response>) {
  const { method, params = {}, options = {}, queryKey } = args;
  const { ...queryOptions } = options;
  const isInMockScope = useIsInMockScope();

  const isMock = getMock(method, params as Args, isInMockScope);
  const _queryKey = queryKey ?? flattenDeep([method.name, ...values(params)]);

  const queryFn = !isEmpty(isMock)
    ? () => {
        const data = getMockData<Args, Response>(method, params as Args);
        return Promise.resolve(data);
      }
    : (ctx: { signal?: AbortSignal }) =>
        method(
          params as Args,
          ctx?.signal ? { signal: ctx.signal } : undefined,
        );

  const { data, isLoading, isFetching, refetch, error, isError, isSuccess } =
    orgUseQuery<Response, AxiosError<TlxHttpResponse>, Response, QueryKey>({
      queryKey: _queryKey,
      queryFn,
      ...queryOptions,
    });

  return {
    data,
    isLoading,
    isFetching,
    refetch,
    queryKey: _queryKey,
    error,
    isError,
    isSuccess,
  };
}

export const useClearQuery = () => {
  const queryClient = useQueryClient();
  return {
    /** Each entry may be a full query key array or a single string segment. */
    remove: (queryKeys: Array<QueryKey | string>) =>
      queryKeys.forEach(key => {
        const queryKey = (Array.isArray(key) ? key : [key]) as QueryKey;
        queryClient.removeQueries({ queryKey });
      }),
    /**
     * Refetch instead of dropping — for reads a *currently mounted* surface is
     * displaying. `remove` deletes the cache entry, so those observers fall back
     * to `data === undefined` until the refetch lands and briefly render their
     * empty/not-configured states; invalidating keeps the last value on screen.
     *
     * `refetchType: "all"` because the app sets `refetchOnMount: false` globally
     * (`src/index.tsx`): an inactive query that is only *marked* stale would never
     * refetch on its next mount and would serve the pre-save value. That makes
     * this a strict superset of what `remove` guaranteed.
     */
    invalidate: (queryKeys: Array<QueryKey | string>) =>
      queryKeys.forEach(key => {
        const queryKey = (Array.isArray(key) ? key : [key]) as QueryKey;
        void queryClient.invalidateQueries({ queryKey, refetchType: "all" });
      }),
    removeQueriesByAccountId: (accountId: string) => {
      queryClient.removeQueries({
        predicate: query => {
          const key = query.queryKey;
          return key.includes(accountId);
        },
      });
    },
  };
};

export const useUpdateCache = () => {
  const queryClient = useQueryClient();
  return {
    update: <T>(queryKey: QueryKey, data: T) =>
      queryClient.setQueriesData({ queryKey }, data),
  };
};

export default useQuery;
